/**
 * The toast a patient reads when a submit is blocked.
 *
 * WHY THIS EXISTS
 * The toast used to be hardcoded "Please complete the required field: X" for EVERY
 * failure. A patient who typed "abc" into a phone field read "please complete" about a
 * field they had already completed, with no hint of what was actually wrong — the reason
 * was only in the small red line under the control, which is off-screen on a phone when
 * the toast fires. Naming the field AND repeating its message is the whole point.
 *
 * ⛔ IMPORT-FREE ON PURPOSE — cypress/node-tests/forms-format-rules.test.js loads and
 * executes THIS EXACT FILE rather than mirroring it. Keep it dependency-free.
 */

/**
 * Build the multi-line toast body.
 *
 * @param {Array<{label: string, messages: string[]}>} entries one per failing control
 * @param {{single: string, plural: string}} headings localized headings (see systemTranslations)
 * @returns {string} "<heading>\n\n• Label — message" (one bullet per field)
 *
 * @example
 * buildValidationToastMessage(
 *   [{ label: 'Cell Phone', messages: ['Enter a valid 10-digit phone number.'] }],
 *   { single: 'Please check this field:', plural: 'Please check the highlighted fields:' }
 * )
 * // "Please check this field:\n\n• Cell Phone — Enter a valid 10-digit phone number."
 */
export function buildValidationToastMessage(entries, headings) {
    const list = Array.isArray(entries) ? entries.filter(e => e && e.label) : []
    if (!list.length) {
        return ''
    }

    const safeHeadings = headings || {}
    const heading = list.length === 1
        ? (safeHeadings.single || '')
        : (safeHeadings.plural || '')

    const bullets = list.map(entry => {
        const messages = Array.isArray(entry.messages)
            ? entry.messages.filter(m => typeof m === 'string' && m.trim() !== '')
            : []

        // No message on the rule (a hand-edited ConfigData) — the field name alone is
        // still more useful than a bullet that trails off into an em dash.
        if (!messages.length) {
            return `• ${entry.label}`
        }

        return `• ${entry.label} — ${messages.join(' ')}`
    })

    return `${heading}\n\n${bullets.join('\n')}`
}
